<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import ImageDetailSummary from './ImageDetailSummary.vue'
import ImageDetailDefects from './ImageDetailDefects.vue'
import { CaretUpIcon } from '@/common/components/elements/icons/Icons'
import { ElCollapse, ElCollapseItem, ElButton } from 'element-plus'
//Store
import { useImageReviewStore } from 'Store/pages/imageReview/imageReviewStore'
import { useWaypointsStore } from 'Store/pages/imageReview/waypointsStore'
//Types
import { formatDate } from 'Common/utils/formatDate'
import TriangleExclamationIcon from 'Common/components/elements/icons/TriangleExclamationIcon.vue'

const store = useImageReviewStore()
const waypointsStore = useWaypointsStore()
const { currentImage } = storeToRefs(store.imagesStore)
const { isDefectDetailsVisible } = storeToRefs(store.interfaceStore)
const {
  stagedAnnotations,
  isAllStagedAnnotationInSameGroup,
  isStagedAnnotationsInDifferentGroups,
  isStagedAnnotationsConsistent,
} = storeToRefs(store.annotationsStore)
const { currentWaypoint } = storeToRefs(waypointsStore)
const defaultActiveSections = ['defects', 'suggestions']
const activeSections = ref(defaultActiveSections)
const isGroupButtonHovered = ref<boolean>(false)

const handleActiveSectionsChange = (newActiveSections: Array<string>) => {
  activeSections.value = Array.from(newActiveSections)
}

const handleEditButtonClick = () => {
  store.annotationsStore.clearHoveredAnnotations()
  isDefectDetailsVisible.value = true
}

const handleGroupButtonClick = () => {
  if (requiresEdit.value === false) store.serverStore.handleGroupSubmit()
  else {
    store.annotationsStore.clearHoveredAnnotations()
    store.annotationsStore.groupAnnotations = true
    isDefectDetailsVisible.value = true
  }
}

const handleRemoveFromGroupButtonClick = () => {
  store.serverStore.handleRemoveGroupSubmit()
}

const handleGroupButtonMouseEnter = () => {
  isGroupButtonHovered.value = true
}

const handleGroupButtonMouseLeave = () => {
  isGroupButtonHovered.value = false
}

const capturedAt = computed<string>(() => formatDate(currentImage.value?.capturedAt))
const allowEdit = computed(() => stagedAnnotations.value.length > 0 && isDefectDetailsVisible.value === false)
const stagedAnnotationsContainSuggestions = computed<boolean>(
  () => !stagedAnnotations.value.every((annotation) => annotation?.isAnnotation === true)
)
const allowGroup = computed(
  () => stagedAnnotations.value.length > 0 && isDefectDetailsVisible.value === false
  // && stagedAnnotationsContainSuggestions.value === false
)
const allowAddGroup = computed(
  () =>
    allowGroup.value === true &&
    isAllStagedAnnotationInSameGroup.value === false &&
    isStagedAnnotationsInDifferentGroups.value === false
)
const requiresEdit = computed(() => isStagedAnnotationsConsistent.value === false)
const allowRemoveGroup = computed(() => allowGroup.value === true && isAllStagedAnnotationInSameGroup.value === true)
</script>

<template>
  <div id="image-detail" class="relative h-full flex flex-col border-l-[1px] border-black bg-white">
    <template v-if="currentImage">
      <div class="relative grow shrink w-full min-h-0 max-h-full">
        <el-collapse
          v-model="defaultActiveSections"
          class="relative h-full min-h-0 flex flex-col justify-start"
          @change="handleActiveSectionsChange"
        >
          <el-collapse-item name="info">
            <template #title>
              <div class="px-4 py-2 flex flex-col justify-center items-start w-full">
                <span class="text-lg font-semibold text-gray-900 block leading-tight">
                  {{ currentImage?.imageName }}
                </span>

                <div class="flex items-center gap-3 mt-1">
                  <span class="text-xs text-gray-500">{{ capturedAt }}</span>

                  <span class="text-gray-300 text-xs">|</span>

                  <a
                    :href="currentImage?.imageUrl"
                    target="_blank"
                    download
                    class="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 cursor-pointer transition-colors"
                    @click.stop
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="w-3 h-3"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                      />
                    </svg>
                    Download
                  </a>
                </div>
              </div>
            </template>

            <ImageDetailSummary :image="currentImage" :waypoint="currentWaypoint" />
          </el-collapse-item>
          <ImageDetailDefects :image="currentImage" :active-sections="activeSections" />
        </el-collapse>
      </div>
      <div class="relative h-12 w-full flex justify-center items-center bg-neutral-300">
        <el-button class="edit-button grow" :disabled="!allowEdit" @click="handleEditButtonClick"> Edit </el-button>
        <el-dropdown class="grow" :disabled="!allowGroup" placement="top">
          <el-button
            class="edit-button grow"
            :disabled="!allowGroup"
            @mouseenter="handleGroupButtonMouseEnter"
            @mouseleave="handleGroupButtonMouseLeave"
          >
            Group
            <CaretUpIcon v-if="!stagedAnnotationsContainSuggestions" class="ml-2" />
            <TriangleExclamationIcon v-else class="ml-2" />
          </el-button>
          <template #dropdown>
            <el-dropdown-item :disabled="!allowAddGroup" @click="handleGroupButtonClick">
              <template v-if="!requiresEdit"> Group </template>
              <template v-else> Edit and Group </template>
            </el-dropdown-item>
            <el-dropdown-item :disabled="!allowRemoveGroup" @click="handleRemoveFromGroupButtonClick"
              >Remove From Group</el-dropdown-item
            >
          </template>
        </el-dropdown>
      </div>
    </template>
  </div>
</template>

<style>
/** Increase first header to support image name & capturedAt date */
.el-collapse-item:first-child .el-collapse-item__header {
  height: 4rem;
}
.el-icon.el-collapse-item__arrow {
  color: #0284c7;
  font-size: 24px;
}
.el-collapse-item__header {
  background-color: #dadada !important;
  border-radius: 0px;
}

/**Needed to keep defect list from expanding past parent size*/
.el-collapse-item__wrap {
  height: 100%;
  position: relative;
}
.el-collapse-item__content {
  height: calc(100% - var(--el-collapse-header-height));
  padding-bottom: 0 !important;
}

.annotation-count {
  border-radius: 4px 0 0 4px;
}

.edit-button {
  height: 3rem !important;
  border-radius: 0 !important;
}
</style>
